/** Qualidade da imagem + resumo do download da animação (curto de propósito). */
import { AlertTriangle, ArrowDown, ArrowUp, Check, LoaderCircle } from 'lucide-react';
import { QUALITY_LABELS, QUALITY_MODES, type QualityMode } from '../goes/budget';
import { formatBytes, formatInt } from '../lib/format';
import { useFrameStorage } from '../goes/frameStorage';
import { usePlanStore } from '../loop/plan';
import { useDetalheStore } from '../detalhe/estado';
import { conexaoEconomica, useConexaoStore } from '../detalhe/conexao';
import { loopLoadAllowed, useLoopStore } from '../loop/store';
import { Hint, ProgressBar, Select, type SelectOption } from './primitives';

const OPTIONS: SelectOption<string>[] = QUALITY_MODES.map((q) => ({
  value: String(q),
  label: q === 'auto' ? 'Automática (acompanha o zoom)' : `${QUALITY_LABELS[q]} — ${q} px`,
}));

export function QualitySection() {
  const quality = useLoopStore((s) => s.quality);
  const plan = usePlanStore((s) => s.plan);
  const progress = usePlanStore((s) => s.progress);
  const playWidth = usePlanStore((s) => s.playWidth);
  const decodeWindow = usePlanStore((s) => s.decodeWindow);
  const bitmapWidth = usePlanStore((s) => s.bitmapWidth);
  const fileWidth = playWidth ?? plan?.width ?? null;
  // A animação toca num bitmap menor que o arquivo quando a tela não mostra mais que isso.
  const reduced = bitmapWidth !== null && fileWidth !== null && bitmapWidth < fileWidth;
  const storage = useFrameStorage();

  // Antes do primeiro play só o quadro na tela é baixado (o portal abre parado).
  const loopAll = useLoopStore((s) => loopLoadAllowed(s));
  const downloading = progress.total > 0 && progress.downloaded + progress.failed < progress.total;
  // Baixando uma largura diferente da que está na tela (ex.: melhor, pelo zoom).
  const upgrading = !!plan && !!playWidth && playWidth !== plan.width;
  // Detalhes técnicos só na dica (title), para o painel ficar enxuto.
  const where =
    storage.mode === 'disco' && storage.diskBudget
      ? `no disco do navegador (até ${formatBytes(storage.diskBudget)})`
      : 'na memória do navegador';
  const details = plan
    ? `Nesta qualidade cabem até ${formatInt(plan.capacity)} quadros, guardados ${where}; ${decodeWindow} ficam prontos para desenhar de cada vez.`
    : undefined;

  return (
    <div className="space-y-2.5">
      <Select
        label="Qualidade da imagem"
        value={String(quality)}
        options={OPTIONS}
        onChange={(v) => useLoopStore.getState().setSettings({ quality: v === 'auto' ? 'auto' : (Number(v) as QualityMode) })}
      />
      {plan && (
        <div className="space-y-1.5 rounded-lg border border-ink-700/70 bg-ink-850/60 p-3 text-xs text-ink-300" title={details}>
          <p className="flex items-baseline justify-between gap-2">
            <span>
              <strong className="text-ink-100">{formatInt(plan.frames.length)} quadros</strong>
              {plan.layerCount > 1 && <> × {plan.layerCount} camadas</>} · {formatBytes(plan.bytesTotal)}
            </span>
            <span
              className="flex shrink-0 items-center gap-1 text-ink-200 tabular-nums"
              title={reduced ? `Arquivo de ${fileWidth} px; a animação é desenhada em ${bitmapWidth} px` : 'Largura das imagens'}
            >
              {reduced ? (
                <>
                  {bitmapWidth} <span className="text-ink-400">de {fileWidth} px</span>
                </>
              ) : (
                <>{fileWidth} px</>
              )}
              {upgrading && (
                <span
                  className="flex items-center gap-0.5 text-mata-300"
                  title={plan.width > (playWidth ?? 0) ? 'Baixando a versão melhor' : 'Baixando a nova qualidade'}
                >
                  {plan.width > (playWidth ?? 0) ? <ArrowUp className="size-3" aria-hidden /> : <ArrowDown className="size-3" aria-hidden />}
                  {plan.width}
                  <LoaderCircle className="size-3 animate-spin" aria-hidden />
                </span>
              )}
            </span>
          </p>
          {quality === 'auto' ? (
            <p className="text-ink-400">Melhora ao aproximar o zoom, equilibrando nitidez e processador.</p>
          ) : (
            reduced && <p className="text-ink-400">A tela mostra no máximo {bitmapWidth} px nesta vista; aproxime para ver o arquivo inteiro.</p>
          )}
          <DetalheLinha />
          <p className="text-ink-400">Imagens guardadas {where}.</p>
          {progress.total > 0 && !loopAll && downloading && (
            <p className="text-ink-300" role="status">
              Mostrando a imagem mais recente. As demais imagens do período baixam quando você toca a animação (▶).
            </p>
          )}
          {progress.total > 0 &&
            (loopAll || !downloading) &&
            (downloading ? (
              <div className="space-y-1" role="status">
                <ProgressBar value={(progress.downloaded + progress.failed) / progress.total} />
                <p>
                  Baixando {formatInt(progress.downloaded)} de {formatInt(progress.total)} — já dá para tocar.
                </p>
              </div>
            ) : (
              <p className="flex items-center gap-1 text-mata-300">
                <Check className="size-3.5" aria-hidden />
                Tudo baixado
                {progress.failed > 0 && (
                  <span className="text-yellow-300"> · {progress.failed === 1 ? '1 falhou' : `${formatInt(progress.failed)} falharam`}</span>
                )}
              </p>
            ))}
          {plan.truncated && (
            <p className="flex gap-1.5 text-yellow-300" role="status">
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              <span>
                Só cabem {formatInt(plan.capacity)} de {formatInt(plan.requested)} quadros nesta qualidade
                {storage.mode === 'disco' ? ' no espaço que o navegador concede' : ''}; mostrando os mais recentes.
              </span>
            </p>
          )}
        </div>
      )}
      <Hint>Mais qualidade: mais nitidez no zoom e mais dados para baixar.</Hint>
    </div>
  );
}

/**
 * O detalhe em blocos (servidor da máquina do agendador): a área visível em
 * 3600/7200/14400 px sobre a imagem inteira mais leve. Some quando não está em uso.
 */
function DetalheLinha() {
  const nivel = useDetalheStore((s) => s.nivel);
  const { baixados, total, bytes } = useDetalheStore((s) => s.progresso);
  const economica = useConexaoStore((s) => conexaoEconomica(s));
  const motivo = useConexaoStore((s) => s.motivo);
  if (!nivel) return null;
  return (
    <p
      className="text-ink-300 tabular-nums"
      title="O mesmo JPEG da NOAA, cortado sem recompressão: só a parte que aparece na tela é baixada na nitidez máxima."
    >
      <strong className="text-ink-100">Detalhe {nivel} px</strong> da área visível, em blocos · {formatBytes(bytes)}
      {total > baixados && (
        <span className="text-ink-400">
          {' '}
          · {formatInt(baixados)} de {formatInt(total)}
        </span>
      )}
      {economica && <span className="text-ink-400"> · conexão econômica{motivo ? ` (${motivo})` : ''}: até 3600 px</span>}
    </p>
  );
}
